const T = require('prop-types');
const { DATA_FETCHING: Types } = require('../action-types');

const { makeActionCreator } = require('utils/redux-helpers');
const { analyticsTemplates } = require('utils/analytics');

const { isCordovaRunning } = require('utils/cordova');
const { isAppForeground } = require('utils/app-visibility');
const Firebase = require('utils/firebasex');
const CreateCommunicationService = require('services/communication');

const internals = {};

internals.discoverSearchRequestId = 0;

module.exports = (context) => {

    const selectors = context.selectors.all;
    const actions = context.actions;
    const api = context.api.nearpeer;
    const communication = CreateCommunicationService.fromContext(context);
    const redux = context.redux.hooks;
    const fetchCurrentUserAction = {
        request: (request) => ({
            type: Types.FETCH_CURRENT_USER.REQUEST,
            payload: {
                request
            }
        }),
        success: ({ request, result }) => ({
            type: Types.FETCH_CURRENT_USER.SUCCESS,
            payload: {
                request,
                result
            }
        }),
        failure: ({ request, error }) => ({
            type: Types.FETCH_CURRENT_USER.FAILURE,
            payload: {
                request,
                error
            },
            error: true
        })
    };
    const fetchAppEntitiesAction = makeActionCreator(Types.FETCH_APP_ENTITIES, {
        entities: T.arrayOf(T.string), schoolId: T.number, managedUserId: T.string, verificationToken: T.string
    }, { async: api.dataFetching.fetchAppEntities });
    const fetchAppEntities = Object.assign((params) => async (dispatch, getState) => {

        const session = internals.getRequestSession(selectors, getState());

        return fetchAppEntitiesAction(params)((action) => {

            if (action.type !== Types.FETCH_APP_ENTITIES.REQUEST
                && !internals.isCurrentRequestSession(selectors, getState(), session)) {
                return undefined;
            }

            return dispatch(action);
        }, getState);
    }, fetchAppEntitiesAction);

    const makeSessionAction = (type, props, options) => {

        const base = makeActionCreator(type, props, options);

        return Object.assign((params) => (dispatch, getState) => {

            const session = internals.getRequestSession(selectors, getState());
            const isCurrent = () => internals.isCurrentRequestSession(selectors, getState(), session);
            const scoped = makeActionCreator(type, props, {
                ...options,
                after: options.after ? (details) => isCurrent() && options.after(details) : undefined
            });

            return scoped(params)((action) => {

                if (action.type !== type.REQUEST && !isCurrent()) {
                    return undefined;
                }

                return dispatch(action);
            }, getState);
        }, base);
    };

    const resultPayload = (payload) => ({ result: payload });

    const fetchUnknownMessageAuthors = ({ payload }) => {

        const state = redux.getState();

        const {
            entities: { messages },
            result: sids
        } = payload.result;

        const ids = sids
            .map((sid) => messages[sid].author)
            .filter((value, i, arr) => arr.indexOf(value) === i) // uniqueify
            .filter((id) => !!id && !selectors.userExists(state, id));

        if (!ids.length) {
            return;
        }

        redux.dispatch(actions.dataFetching.fetchUsers({ ids }));
    };

    const prevAnnouncementCounts = {};
    const requestedMissingOfferIds = new Set();
    let lastOfferNotificationSchoolId;
    let previousNotificationOfferIds = new Set();

    const getNotificationOfferIds = (payload) => {

        const notifications = payload.result?.notifications?.entities?.notifications || {};

        return Object.values(notifications)
            .filter((notification) => notification?.type === 'offer' && notification.info?.offerId != null)
            .map((notification) => Number(notification.info.offerId))
            .filter((offerId) => !Number.isNaN(offerId));
    };

    const maybeFetchOffersFromNotifications = ({ dispatch, payload, state }) => {

        const currentSchool = selectors.getCurrentSchool ? selectors.getCurrentSchool(state) : null;

        if (currentSchool && !currentSchool.offersEnabled) {
            return;
        }

        const schoolId = selectors.getCurrentSchoolId ? selectors.getCurrentSchoolId(state) : null;

        if (schoolId !== lastOfferNotificationSchoolId) {
            lastOfferNotificationSchoolId = schoolId;
            requestedMissingOfferIds.clear();
            previousNotificationOfferIds = new Set();
        }

        const notificationOfferIds = getNotificationOfferIds(payload);
        const currentNotificationOfferIds = new Set(notificationOfferIds);
        const existingOfferIds = new Set(
            (selectors.getOffers ? selectors.getOffers(state) : [])
                .map((offer) => Number(offer.id))
        );
        const addedOfferIds = notificationOfferIds.filter((offerId) => {

            return !previousNotificationOfferIds.has(offerId);
        });
        const removedOfferIds = [...previousNotificationOfferIds].filter((offerId) => {

            return !currentNotificationOfferIds.has(offerId);
        });
        const missingOfferIds = addedOfferIds.filter((offerId) => {

            return !existingOfferIds.has(offerId) && !requestedMissingOfferIds.has(offerId);
        });
        const removedLoadedOfferIds = removedOfferIds.filter((offerId) => existingOfferIds.has(offerId));

        previousNotificationOfferIds = currentNotificationOfferIds;
        removedOfferIds.forEach((offerId) => requestedMissingOfferIds.delete(offerId));
        missingOfferIds.forEach((offerId) => requestedMissingOfferIds.add(offerId));

        if (!missingOfferIds.length && !removedLoadedOfferIds.length) {
            return;
        }

        dispatch(actions.dataFetching.fetchOffers());
    };

    const afterNotifications = ({ dispatch, payload }) => {

        const state = redux.getState();
        const isAuthenticated = selectors.getIsAuthenticated(state);
        const isMainAppLoading = selectors.getMainAppLoading
            ? selectors.getMainAppLoading(state)
            : false;

        if (!isAuthenticated || isMainAppLoading || payload.request?.startedWhileMainAppLoading) {
            return;
        }

        const newCounts = payload.result?.currentCommunityUnreadCounts || {};
        const groups = selectors.getGroups(state) || [];
        const announcementGroupIds = groups
            .filter((g) => g.isAnnouncement)
            .map((g) => g.id);

        const hasNewAnnouncementMessages = announcementGroupIds.some((id) => {
            const newCount = newCounts[id]?.count || 0;
            const prevCount = prevAnnouncementCounts[id] || 0;
            return newCount > prevCount;
        });

        announcementGroupIds.forEach((id) => {
            prevAnnouncementCounts[id] = newCounts[id]?.count || 0;
        });

        if (hasNewAnnouncementMessages) {
            dispatch(actions.dataFetching.fetchAnnouncementConversations());
        }

        maybeFetchOffersFromNotifications({ dispatch, payload, state });
    };

    const fetchUnknownLastMessageAuthors = ({ payload }) => {

        const state = redux.getState();
        const lastMessages = payload.result.result;

        if (!lastMessages) {
            return;
        }

        const ids = Object.values(lastMessages)
            .filter((msg) => msg && msg.author)
            .map((msg) => msg.author)
            .filter((value, i, arr) => arr.indexOf(value) === i)
            .filter((id) => !selectors.userExists(state, id));

        if (!ids.length) {
            return;
        }

        redux.dispatch(actions.dataFetching.fetchUsers({ ids }));
    };

    const fetchUnknownAnnouncementAuthors = ({ payload }) => {

        const state = redux.getState();
        const conversations = payload.result?.entities?.announcementConversations;

        if (!conversations) {
            return;
        }

        const ids = Object.values(conversations)
            .filter((conv) => conv && conv.lastMessage && conv.lastMessage.author)
            .map((conv) => conv.lastMessage.author)
            .filter((value, i, arr) => arr.indexOf(value) === i)
            .filter((id) => !selectors.userExists(state, id));

        if (!ids.length) {
            return;
        }

        redux.dispatch(actions.dataFetching.fetchUsers({ ids }));
    };

    return {
        fetchAppEntities,
        fetchSchoolByVerificationToken: makeActionCreator(Types.FETCH_SCHOOL_BY_TOKEN, { token: T.string }, { async: api.dataFetching.getSchoolByVerificationToken }),
        fetchAnnouncementConversation: makeActionCreator(Types.FETCH_GROUP_CONVERSATION, { id: T.string }, { async: api.communication.getAnnouncementConversation }),
        fetchAnnouncementConversations: makeSessionAction(Types.FETCH_ANNOUNCEMENT_CONVERSATIONS, {}, {
            async: api.communication.getAnnouncementConversations,
            after: ({ payload }) => fetchUnknownAnnouncementAuthors({ payload })
        }),
        fetchAnnouncementMessages: makeActionCreator(Types.FETCH_GROUP_CONVERSATION_MESSAGES, { id: T.string }, { async: api.communication.getAnnouncementMessages }),
        fetchSchools: makeActionCreator(Types.FETCH_SCHOOLS, {}, { async: api.dataFetching.getSchools }),
        fetchInterests: makeActionCreator(Types.FETCH_INTERESTS, {}, { async: api.dataFetching.getInterests }),
        fetchCustomInterests: makeActionCreator(Types.FETCH_INTERESTS, { schoolId: T.number }, { async: api.dataFetching.getCustomInterests }),
        fetchRoleGroups: makeActionCreator(Types.FETCH_ROLE_GROUPS, {}, { async: api.dataFetching.getRoleGroups }),
        fetchRoles: makeActionCreator(Types.FETCH_ROLES, {}, { async: api.dataFetching.getRoles }),
        fetchCategories: makeActionCreator(Types.FETCH_CATEGORIES, {}, { async: api.dataFetching.getCategories }),
        fetchBadgeTypes: makeActionCreator(Types.FETCH_BADGE_TYPES, { schoolId: T.number }, { async: api.dataFetching.getBadges }),
        fetchConversationStarters: makeActionCreator(Types.FETCH_CONVERSATION_STARTERS, { schoolId: T.number }, { async: api.dataFetching.getConversationStarters }),
        fetchYearsHired: makeActionCreator(Types.FETCH_YEARS_HIRED, { schoolId: T.number }, { async: api.dataFetching.getYearsHired }),
        fetchOffices: makeActionCreator(Types.FETCH_OFFICES, { schoolId: T.number }, { async: api.dataFetching.getOffices }),
        fetchPreferences: makeActionCreator(Types.FETCH_USER_PREFERENCES, {}, { async: api.dataFetching.getUserPreferences }),
        fetchUsers: makeActionCreator(Types.FETCH_USERS, { ids: T.array }, { async: api.dataFetching.getUsers }),
        fetchUserNames: makeActionCreator(Types.FETCH_USERS, { ids: T.array }, {
            async: async (params) => {

                const result = await api.dataFetching.getUsers(params);

                if (result && result.entities && result.entities.users) {
                    const users = result.entities.users;
                    const userNames = {};

                    params.ids.forEach((id) => {

                        if (users[id]) {
                            userNames[id] = `${users[id].firstName} ${users[id].lastName}`.trim() || 'Unknown';
                        }
                    });

                    return { result: userNames };
                }

                return { result: {} };
            }
        }),
        fetchUser: makeActionCreator(Types.FETCH_USER, { id: T.string, debugMatching: T.bool }, { async: api.dataFetching.getUser }),
        fetchCurrentUser: (params = {}) => async (dispatch, getState) => {

            const session = internals.getRequestSession(selectors, getState());
            const request = internals.getLoadingAwareRequest({
                params,
                selectors,
                state: getState()
            });

            dispatch(fetchCurrentUserAction.request(request));

            try {
                const result = await api.dataFetching.getCurrentUser(request, { dispatch, getState });

                if (!internals.isCurrentRequestSession(selectors, getState(), session) || internals.shouldSkipCurrentUserProcessing({
                    request,
                    selectors,
                    state: getState()
                })) {
                    return undefined;
                }

                const dispatchResult = dispatch(fetchCurrentUserAction.success({ request, result }));

                await internals.afterFetchCurrentUser({ payload: { request, result } });

                return dispatchResult;
            }
            catch (error) {

                if (!internals.isCurrentRequestSession(selectors, getState(), session) || internals.shouldSkipCurrentUserProcessing({
                    request,
                    selectors,
                    state: getState()
                })) {
                    return undefined;
                }

                dispatch(fetchCurrentUserAction.failure({ request, error }));

                if (params.throwOnError) {
                    throw error;
                }

                return undefined;
            }
        },
        fetchClassUserSearchResults: {
            firstPage: makeActionCreator(
                Types.FETCH_CLASS_USER_SEARCH_RESULTS.FIRST_PAGE,
                {
                    allByMatches: T.bool,
                    debugMatching: T.bool,
                    sortType: T.string,
                    loadedUsersIds: T.array,
                    preserveExistingResults: T.bool,
                    viewAll: T.bool,
                    major: T.string,
                    interests: T.array,
                    passionInterests: T.array,
                    name: T.string,
                    age: T.number,
                    radius: T.number,
                    isOnline: T.bool,
                    isVeteran: T.bool,
                    isTransfer: T.bool,
                    fullTimeStatus: T.string,
                    locationType: T.oneOf(['housing', 'hometown']),
                    customLocation: T.shape({
                        id: T.any,
                        placeId: T.string,
                        name: T.string
                    }),
                    incomingClass: T.string,
                    graduatingClass: T.string,
                    classId: T.number,
                    includeStaff: T.bool,
                    staffTitle: T.string,
                    studentName: T.string,
                    contextFilter: T.string,
                    contextFilterType: T.string,
                    career: T.string,
                    profession: T.string,
                    bio: T.string,
                    title: T.string,
                    departmentId: T.number,
                    openSocial: T.bool,
                    workRemote: T.bool,
                    yearHiredId: T.number,
                    officeId: T.number,
                    badge: T.string
                },
                {
                    async: internals.getUserSearchAsyncFunc({
                        api,
                        selectors,
                        redux,
                        actions,
                        roleGroupFilterStorageKey: 'groups'
                    }, { limit: 20 })
                }
            ),
            nextPage: makeActionCreator(Types.FETCH_CLASS_USER_SEARCH_RESULTS.NEXT_PAGE, { classId: T.number }, {
                async: ({ classId }) => {

                    const criteria = selectors.getNextPageCriteria_forClassSearch(
                        redux.getState(),
                        classId
                    );

                    if (!criteria) {
                        return Promise.reject(new Error('Can\'t currently page through user search results'));
                    }

                    return api.dataFetching.getUserSearchResults(criteria);
                }
            }),
            mentions: makeActionCreator(Types.FETCH_CLASS_USER_SEARCH_RESULTS.MENTIONS, { classId: T.number, name: T.string }, {
                async: ({ classId, name }) => {

                    return api.dataFetching.searchClassUsers_byName({ classId, name });
                }
            })
        },
        fetchDiscoverSearchResults: {
            firstPage: makeActionCreator(
                Types.FETCH_USER_SEARCH_RESULTS.FIRST_PAGE,
                {
                    allByMatches: T.bool,
                    debugMatching: T.bool,
                    sortType: T.string,
                    loadedUsersIds: T.array,
                    viewAll: T.bool,
                    major: T.arrayOf(T.oneOfType([T.number, T.string])),
                    interests: T.array,
                    passionInterests: T.array,
                    name: T.string,
                    age: T.number,
                    radius: T.number,
                    isOnline: T.bool,
                    isVeteran: T.bool,
                    isTransfer: T.bool,
                    searchIsParent: T.bool,
                    fullTimeStatus: T.string,
                    locationType: T.oneOf(['housing', 'hometown', 'custom']),
                    customLocation: T.shape({
                        id: T.any,
                        placeId: T.string,
                        name: T.string
                    }),
                    incomingClass: T.string,
                    graduatingClass: T.string,
                    classId: T.number,
                    includeStaff: T.bool,
                    staffTitle: T.string,
                    studentName: T.string,
                    contextFilter: T.string,
                    contextFilterType: T.string,
                    searchRequestId: T.number,
                    career: T.string,
                    profession: T.string,
                    bio: T.string,
                    title: T.string,
                    departmentId: T.number,
                    openSocial: T.bool,
                    workRemote: T.bool,
                    yearHiredId: T.number,
                    officeId: T.number,
                    badge: T.string
                },
                {
                    requestPropTransform: (request) => ({
                        ...request,
                        searchRequestId: ++internals.discoverSearchRequestId
                    }),
                    async: internals.getUserSearchAsyncFunc({
                        api,
                        selectors,
                        redux,
                        actions,
                        roleGroupFilterStorageKey: 'discover'
                    }, { limit: 20 })
                }
            ),
            nextPage: makeActionCreator(Types.FETCH_USER_SEARCH_RESULTS.NEXT_PAGE, {
                searchRequestId: T.number
            }, {
                requestPropTransform: (_request, { state }) => ({
                    searchRequestId: selectors.getCurrentSearchRequestId_forUserSearchResults(state)
                }),
                async: () => {

                    const criteria = selectors.getNextPageCriteria_forUserSearchResults(redux.getState());

                    if (!criteria) {
                        return Promise.reject(new Error('Can\'t currently page through user search results'));
                    }

                    return api.dataFetching.getUserSearchResults(criteria);
                }
            })
        },
        fetchUserSearchNotifyResults: makeActionCreator(
            Types.FETCH_USER_SEARCH_NOTIFY_RESULTS,
            {
                allByMatches: T.bool,
                sortType: T.string,
                loadedUsersIds: T.array,
                viewAll: T.bool,
                major: T.string,
                interests: T.array,
                passionInterests: T.array,
                name: T.string,
                age: T.number,
                radius: T.number,
                isOnline: T.bool,
                isVeteran: T.bool,
                isTransfer: T.bool,
                searchIsParent: T.bool,
                fullTimeStatus: T.string,
                locationType: T.oneOf(['housing', 'hometown']),
                incomingClass: T.string,
                graduatingClass: T.string,
                classId: T.number,
                includeStaff: T.bool,
                staffTitle: T.string,
                studentName: T.string,
                contextFilter: T.string,
                career: T.string,
                profession: T.string,
                bio: T.string,
                title: T.string,
                departmentId: T.number,
                openSocial: T.bool,
                workRemote: T.bool,
                yearHiredId: T.number,
                officeId: T.number,
                badge: T.string
            },
            {
                async: internals.getUserSearchAsyncFunc({
                    api,
                    selectors,
                    redux,
                    actions,
                    basicSearch: true,
                    searchType: 'notify',
                    contextFilterType: 'role',
                    roleGroupFilterStorageKey: 'admin'
                }, { limit: -1 })
            }
        ),
        fetchUserSearchSurveyResults: makeActionCreator(
            Types.FETCH_USER_SEARCH_SURVEY_RESULTS,
            {
                allByMatches: T.bool,
                sortType: T.string,
                loadedUsersIds: T.array,
                viewAll: T.bool,
                major: T.string,
                interests: T.array,
                passionInterests: T.array,
                name: T.string,
                age: T.number,
                radius: T.number,
                isOnline: T.bool,
                isVeteran: T.bool,
                isTransfer: T.bool,
                searchIsParent: T.bool,
                fullTimeStatus: T.string,
                locationType: T.oneOf(['housing', 'hometown']),
                incomingClass: T.string,
                graduatingClass: T.string,
                classId: T.number,
                includeStaff: T.bool,
                staffTitle: T.string,
                studentName: T.string,
                contextFilter: T.string,
                career: T.string,
                profession: T.string,
                bio: T.string,
                title: T.string,
                departmentId: T.number,
                openSocial: T.bool,
                workRemote: T.bool,
                yearHiredId: T.number,
                officeId: T.number,
                badge: T.string
            },
            {
                async: internals.getUserSearchAsyncFunc({
                    api,
                    selectors,
                    redux,
                    actions,
                    basicSearch: true,
                    searchType: 'survey',
                    contextFilterType: 'role',
                    roleGroupFilterStorageKey: 'admin'
                }, { limit: -1 })
            }
        ),
        fetchChannel: makeActionCreator(Types.FETCH_CHANNEL, { channelSid: T.string }, { async: communication.getConversation }),
        fetchChannels: makeSessionAction(Types.FETCH_CHANNELS, {}, { async: communication.getChannels }),
        fetchChannelLastMessage: makeActionCreator(Types.FETCH_CHANNEL_LAST_MESSAGE, { channelSid: T.string }, { async: communication.getConversationLastMessage }),
        fetchAllChannelLastMessages: makeSessionAction(Types.FETCH_ALL_CHANNEL_LAST_MESSAGES, {}, {
            async: api.communication.getAllChannelLastMessages,
            after: ({ payload }) => fetchUnknownLastMessageAuthors({ payload })
        }),
        clearChannelLastMessage: makeActionCreator(Types.CLEAR_CHANNEL_LAST_MESSAGE, { channelSid: T.string }),
        updateChannelLastMessage: makeActionCreator(Types.UPDATE_CHANNEL_LAST_MESSAGE, { channelSid: T.string, message: T.object }),
        fetchMessages: {
            firstPage: makeActionCreator(Types.FETCH_MESSAGES.FIRST_PAGE, { channelSid: T.string }, {
                async: communication.getMessages,
                after: ({ payload, dispatch }) => {

                    dispatch(actions.dataFetching.fetchMessages.hasPrevPage({
                        channelSid: payload.request.channelSid
                    }));

                    return fetchUnknownMessageAuthors({ payload });
                }
            }),
            nextPage: makeActionCreator(Types.FETCH_MESSAGES.NEXT_PAGE, { channelSid: T.string }, {
                async: communication.getMoreMessages,
                after: ({ payload, dispatch }) => {

                    dispatch(actions.dataFetching.fetchMessages.hasPrevPage({
                        channelSid: payload.request.channelSid
                    }));

                    return fetchUnknownMessageAuthors({ payload });
                }
            }),
            hasPrevPage: makeActionCreator(Types.FETCH_MESSAGES.HAS_PREV_PAGE, { channelSid: T.string }, {
                async: communication.getHasPrevPage
            })
        },
        subscribeMessages: {
            update: makeActionCreator(Types.SUBSCRIBE_MESSAGES.UPDATE, { entities: T.object, result: T.any }, { propTransform: resultPayload }),
            remove: makeActionCreator(Types.SUBSCRIBE_MESSAGES.REMOVE, { sid: T.string })
        },
        fetchSync: () => async (dispatch, getState) => {

            const state = getState();

            if (!isAppForeground({ selectors, state })) {
                return undefined;
            }

            const request = internals.getLoadingAwareRequest({ params: {}, selectors, state });
            const token = selectors.getApiToken(state);
            const schoolId = selectors.getCurrentSchoolId(state);
            const currentUser = selectors.getCurrentUser(state)?.user();
            const currentUserRevision = currentUser?.syncRevision ?? null;

            try {
                const result = await api.dataFetching.sync({ currentUserRevision });

                const latestState = getState();

                if (!selectors.getIsAuthenticated(latestState) ||
                    selectors.getApiToken(latestState) !== token ||
                    selectors.getCurrentSchoolId(latestState) !== schoolId) {
                    return undefined;
                }

                const notificationPayload = { request, result: result.notifications };
                dispatch({ type: Types.FETCH_NOTIFICATIONS.SUCCESS, payload: notificationPayload });
                afterNotifications({ dispatch, payload: notificationPayload });

                const latestRevision = selectors.getCurrentUser(getState())?.user()?.syncRevision;

                if (result.currentUser && (latestRevision == null ||
                    Number(result.currentUserRevision) >= Number(latestRevision))) {
                    dispatch(fetchCurrentUserAction.success({ request, result: result.currentUser }));
                    await internals.afterFetchCurrentUser({ payload: { request, result: result.currentUser } });
                }

                return result;
            }
            catch (error) {
                if (selectors.getIsAuthenticated(getState())) {
                    dispatch({ type: Types.FETCH_SYNC.FAILURE, payload: { request, error }, error: true });
                }

                return undefined;
            }
        },
        fetchNotifications: makeSessionAction(Types.FETCH_NOTIFICATIONS, {
            startedWhileMainAppLoading: T.bool
        }, {
            requestPropTransform: (params = {}) => internals.getLoadingAwareRequest({
                params,
                selectors,
                state: redux.getState()
            }),
            async: api.dataFetching.getNotifications,
            after: afterNotifications
        }),
        fetchClass: makeActionCreator(Types.FETCH_CLASS, { id: T.string }, { async: api.dataFetching.getClass }),
        fetchClasses: makeActionCreator(Types.FETCH_CLASSES, { managedUserId: T.number }, { async: api.dataFetching.getGroups }),
        fetchSurveys: makeActionCreator(Types.FETCH_SURVEYS, {}, { async: api.dataFetching.getSurveys }),
        fetchSurvey: makeActionCreator(Types.FETCH_SURVEY, { id: T.string, includeUserAnswers: T.bool }, { async: api.dataFetching.getSurvey }),
        fetchGroupSearchResults: makeActionCreator(Types.FETCH_CLASS_SEARCH_RESULTS, { searchText: T.string, showAll: T.bool, managedUserId: T.number }, { async: api.dataFetching.getGroupSearchResults }),
        fetchScheduledNotifications: makeActionCreator(Types.GET_SCHEDULED_NOTIFICATIONS, {}, { async: api.dataFetching.getScheduledNotifications }),
        fetchOffers: makeActionCreator(Types.FETCH_OFFERS, {}, { async: api.dataFetching.getOffers }),
        fetchOffer: makeActionCreator(Types.FETCH_OFFER, { id: T.number }, { async: api.dataFetching.getOffer }),
        fetchResources: makeActionCreator(Types.FETCH_RESOURCES, {}, { async: api.dataFetching.getResources }),
        fetchResource: makeActionCreator(Types.FETCH_RESOURCE, { id: T.number }, { async: api.dataFetching.getResource })
    };
};

internals.afterFetchCurrentUser = async ({ payload }) => {

    const {
        result: { entities, result }
    } = payload;

    const currentUser = entities?.users[result?.user];

    if (isCordovaRunning() && currentUser?.needsFirebaseToken) {
        // Ask to grant perms if turned off.
        // Fetch and register new token on the API if needed
        const token = await Firebase.reconcileServerTokenFlagThrottled({
            forceResend: true
        });

        analyticsTemplates.pushNotifs({
            action: 'token_reuploaded',
            label: 'needs_firebase_token_response',
            value: token
        });
    }
};

internals.getRequestSession = (selectors, state) => {

    const authenticated = selectors.getIsAuthenticated?.(state);

    return {
        authenticated,
        token: authenticated ? selectors.getApiToken?.(state) : null,
        schoolId: selectors.getCurrentSchoolId?.(state)
    };
};

internals.isCurrentRequestSession = (selectors, state, session) => {

    const current = internals.getRequestSession(selectors, state);

    return current.authenticated === session.authenticated
        && current.token === session.token
        && current.schoolId === session.schoolId;
};

internals.getLoadingAwareRequest = ({
    params = {},
    selectors,
    state
}) => ({
    ...params,
    startedWhileMainAppLoading: selectors.getMainAppLoading
        ? selectors.getMainAppLoading(state)
        : false
});

internals.shouldSkipCurrentUserProcessing = ({
    request,
    selectors,
    state
}) => {

    if (!request?.skipProcessingWhileMainAppLoading) {
        return false;
    }

    const isMainAppLoading = selectors.getMainAppLoading
        ? selectors.getMainAppLoading(state)
        : false;

    return isMainAppLoading || !!request.startedWhileMainAppLoading;
};

internals.getUserSearchAsyncFunc = (options, pagination) => {

    const {
        api,
        selectors,
        redux,
        actions,
        basicSearch,
        searchType,
        roleGroupFilterStorageKey,
        contextFilterType = 'roleGroup'
    } = options;

    return (props = {}) => {

        const state = redux.getState();
        const searchProps = { ...props };

        delete searchProps.preserveExistingResults;
        delete searchProps.searchRequestId;

        let {
            allByMatches,
            sortType,
            viewAll,
            classId,
            debugMatching,
            ...criteria
        } = searchProps;

        if (!criteria.radius || !criteria.locationType) {
            delete criteria.radius;
            delete criteria.locationType;
        }

        if (roleGroupFilterStorageKey === 'discover' && !viewAll && criteria.contextFilter &&
            Object.keys(criteria).every((key) => ['contextFilter', 'contextFilterType'].includes(key))) {
            allByMatches = true;
        }

        if (allByMatches) {
            criteria = {
                allByMatches: true,
                sortType,
                debugMatching,
                contextFilter: criteria.contextFilter,
                contextFilterType: criteria.contextFilterType
            };
        }
        else if (viewAll) {
            criteria = {};
        }

        criteria.classId = classId;

        const roleGroupFilter = selectors.getRoleGroupFilter(state, roleGroupFilterStorageKey);

        if (roleGroupFilter && roleGroupFilter !== 'all' &&
            (criteria.contextFilter === null || criteria.contextFilter === undefined) &&
            (!allByMatches || roleGroupFilterStorageKey !== 'discover')) {
            criteria.contextFilter = roleGroupFilter;
            criteria.contextFilterType = contextFilterType;
        }

        // Match scoring accepts role filters, but other search fields belong
        // to normal filtered searches.
        if (!allByMatches) {
            criteria.isBasicSearch = Boolean(basicSearch);

            if (criteria.contextFilter && !criteria.contextFilterType) {
                criteria.contextFilterType = contextFilterType;
            }
        }

        if (!allByMatches && !classId && !basicSearch) {
            redux.dispatch(actions.app.setDiscoverSearchFilter({ filter: { ...criteria, sortType } }));
        }
        else if (!allByMatches && basicSearch) {

            if (searchType === 'notify') {
                redux.dispatch(actions.app.setNotifySearchCriteria({ filter: { ...criteria, sortType } }));
            }
            else if (searchType === 'survey') {
                redux.dispatch(actions.app.setSurveySearchCriteria({ filter: { ...criteria, sortType } }));
            }
        }

        analyticsTemplates.buttons('peers sort option', `sort option: peers sort submit ${sortType ? sortType : ''}`);

        return api.dataFetching.getUserSearchResults(
            { ...criteria, sortType },
            pagination,
            basicSearch
        )
            .then((results) => {

                return Object.assign({ criteria }, results);
            });
    };
};

module.exports.internals = internals;
